import { Lock, Check } from 'lucide-react'
import { gsap } from 'gsap'
import { THEMES, THEME_ORDER, useTheme } from '../context/ThemeContext'

/** Mock shell — invisible wrapper, just sizes the mock content to fit the panel. */
function MockShell({ children }: { children: React.ReactNode }) {
  return <div className="relative w-full max-w-[400px]">{children}</div>
}

/** Glass surface for individual mock items (transactions, subscription rows, etc).
    backdrop-filter intentionally omitted — these mocks sit inside Feature cards
    that already have a backdrop-filter blur, and iOS Safari paint cost is
    quadratic in nested glass layers. Bg + border alone reads as glass enough. */
const glassItemStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 30px -15px rgba(0,0,0,0.4)',
}

export function FreeMock() {
  return (
    <MockShell>
      <div className="rounded-xl p-4 text-center" style={glassItemStyle}>
        <div
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-3"
          style={{
            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)',
            boxShadow: '0 10px 30px -10px var(--accent-ring)',
          }}
        >
          <span className="text-bg-0 text-[24px] font-extrabold">$0</span>
        </div>
        <div className="text-[15px] font-semibold text-ink-1 mb-1">All features. No paywall.</div>
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-2">
          {['No ads', 'No trials', 'No login'].map((l) => (
            <span
              key={l}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-[10px] text-ink-2"
            >
              {l}
            </span>
          ))}
        </div>
      </div>
    </MockShell>
  )
}

export function ApplePayMock() {
  return (
    <MockShell>
      <div className="rounded-2xl p-3 flex items-center gap-3" style={glassItemStyle}>
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center shrink-0">
          <span aria-hidden className="text-white text-[18px] leading-none -mt-0.5">{''}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-ink-1 font-medium truncate">Apple Store</div>
          <div className="text-[10px] text-ink-3">Apple Pay · just now</div>
        </div>
        <div className="text-[14px] font-semibold text-ink-1 font-mono">−$1,499</div>
      </div>
      <div className="mt-2 flex items-center justify-center gap-1.5 text-[10px] text-[var(--accent)]">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
        Auto-imported via Shortcut
      </div>
    </MockShell>
  )
}

export function CurrencyMock() {
  return (
    <MockShell>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl p-3" style={glassItemStyle}>
          <div className="text-[10px] text-ink-3 mb-0.5">From</div>
          <div className="text-[18px] font-semibold text-ink-1 font-mono">$1,000</div>
          <div className="text-[11px] text-ink-3 mt-0.5">USD</div>
        </div>
        <div className="rounded-xl p-3" style={glassItemStyle}>
          <div className="text-[10px] text-ink-3 mb-0.5">To</div>
          <div className="text-[18px] font-semibold text-ink-1 font-mono">€915</div>
          <div className="text-[11px] text-[var(--accent)] mt-0.5">EUR · 0.915</div>
        </div>
      </div>
    </MockShell>
  )
}

export function CategoriesMock() {
  const cats = [
    { icon: '🍕', name: 'Food', color: '#fb923c' },
    { icon: '🚇', name: 'Transit', color: '#60a5fa' },
    { icon: '🏠', name: 'Rent', color: '#a78bfa' },
    { icon: '🎬', name: 'Fun', color: '#f472b6' },
    { icon: '💪', name: 'Health', color: '#22c55e' },
    { icon: '✏️', name: 'Edit', color: 'var(--accent)' },
  ]
  return (
    <MockShell>
      <div className="grid grid-cols-3 gap-1.5">
        {cats.map((c) => (
          <div
            key={c.name}
            className="rounded-xl p-2.5 flex flex-col items-center"
            style={glassItemStyle}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center mb-1.5 text-sm"
              style={{ background: `${c.color}26`, border: `1px solid ${c.color}40` }}
            >
              {c.icon}
            </div>
            <div className="text-[10px] text-ink-1 font-medium">{c.name}</div>
          </div>
        ))}
      </div>
    </MockShell>
  )
}

export function SubscriptionMock() {
  return (
    <MockShell>
      <div className="space-y-1.5">
        {[
          { name: 'Netflix', price: '$15.49', renew: 'in 4 days', trial: false },
          { name: 'iCloud+', price: '$2.99', renew: 'trial · 12 days', trial: true },
          { name: 'Spotify', price: '$9.99', renew: 'in 18 days', trial: false },
        ].map((s) => (
          <div
            key={s.name}
            className="rounded-xl p-2.5 flex items-center gap-2.5"
            style={glassItemStyle}
          >
            <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center text-[11px] font-semibold text-ink-1">
              {s.name[0]}
            </div>
            <div className="flex-1">
              <div className="text-[12px] text-ink-1 font-medium">{s.name}</div>
              <div className={`text-[10px] ${s.trial ? 'text-[var(--accent)]' : 'text-ink-3'}`}>
                {s.renew}
              </div>
            </div>
            <div className="text-[13px] font-mono text-ink-1">{s.price}</div>
          </div>
        ))}
      </div>
    </MockShell>
  )
}

export function GoalsMock() {
  const pct = 0.68
  return (
    <MockShell>
      <div className="rounded-xl p-4 flex items-center gap-4" style={glassItemStyle}>
        <svg width="76" height="76" viewBox="0 0 92 92" className="shrink-0">
          <circle cx="46" cy="46" r="38" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
          <circle
            cx="46"
            cy="46"
            r="38"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 38 * pct} ${2 * Math.PI * 38}`}
            transform="rotate(-90 46 46)"
          />
          <text
            x="46"
            y="51"
            textAnchor="middle"
            fontSize="18"
            fontWeight="600"
            fill="#fafafa"
            fontFamily="SF Mono, monospace"
          >
            {Math.round(pct * 100)}%
          </text>
        </svg>
        <div className="flex-1">
          <div className="text-[11px] text-ink-3 mb-0.5">Trip to Japan</div>
          <div className="text-[16px] font-semibold text-ink-1 font-mono">
            $3,400 <span className="text-ink-3 text-[12px]">/ $5,000</span>
          </div>
          <div className="mt-1.5 text-[10px] text-[var(--accent)]">on track</div>
        </div>
      </div>
    </MockShell>
  )
}

export function StreakMock() {
  return (
    <MockShell>
      <div className="rounded-xl p-4" style={glassItemStyle}>
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-xl"
            style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.3)' }}
          >
            🔥
          </div>
          <div>
            <div className="text-[24px] font-semibold text-ink-1 font-mono leading-none">23</div>
            <div className="text-[10px] text-ink-3 mt-0.5">day streak</div>
          </div>
          <div className="ml-auto px-2 py-0.5 rounded-full bg-[var(--accent-soft)] border border-[var(--accent)]/30 text-[10px] text-[var(--accent)] font-medium">
            +50 XP
          </div>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-full"
              style={{ background: i < 5 ? 'var(--accent)' : 'rgba(255,255,255,0.08)' }}
            />
          ))}
        </div>
      </div>
    </MockShell>
  )
}

export function PrivacyMock() {
  return (
    <MockShell>
      <div className="rounded-xl p-4 text-center" style={glassItemStyle}>
        <div
          className="w-12 h-12 rounded-2xl mx-auto mb-2 flex items-center justify-center"
          style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent-ring)' }}
        >
          <Lock size={20} className="text-[var(--accent)]" strokeWidth={1.75} />
        </div>
        <div className="text-[13px] font-semibold text-ink-1 mb-2">On-device only</div>
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {['No tracking', 'No ads', 'Open privacy'].map((l) => (
            <span
              key={l}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] text-ink-2"
            >
              <Check size={9} className="text-[var(--accent)]" strokeWidth={2.5} />
              {l}
            </span>
          ))}
        </div>
      </div>
    </MockShell>
  )
}

export function GlassMock() {
  return (
    <MockShell>
      <div className="space-y-1.5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-xl p-3 flex items-center gap-2.5"
            style={{
              background: `rgba(255,255,255,${0.04 - i * 0.005})`,
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div
              className="w-9 h-9 rounded-lg"
              style={{ background: `linear-gradient(135deg, var(--accent) ${20 * i}%, var(--accent-2) 100%)` }}
            />
            <div className="flex-1">
              <div className="text-[12px] text-ink-1 font-medium">Layer {i}</div>
              <div className="text-[10px] text-ink-3">iOS 26 Glass</div>
            </div>
          </div>
        ))}
      </div>
    </MockShell>
  )
}

export function ThemeMock() {
  const { theme, setTheme } = useTheme()
  return (
    <MockShell>
      <div className="rounded-xl p-4" style={glassItemStyle}>
        <div className="text-[10px] text-ink-3 mb-2 uppercase tracking-widest">Tap to repaint</div>
        <div className="grid grid-cols-3 gap-2">
          {THEME_ORDER.map((name) => {
            const t = THEMES[name]
            const active = theme.name === name
            return (
              <button
                key={name}
                onClick={(e) => {
                  e.stopPropagation()
                  setTheme(name)
                  gsap.fromTo(
                    '.feat-title',
                    { textShadow: '0 0 60px var(--accent-ring)' },
                    { textShadow: '0 0 0 transparent', duration: 1.2, ease: 'power3.out' }
                  )
                }}
                className="aspect-square rounded-lg transition-transform hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${t.accent} 0%, ${t.accent2} 100%)`,
                  boxShadow: active
                    ? `0 0 0 2px #09090b, 0 0 0 3px ${t.accent}`
                    : '0 0 0 1px rgba(255,255,255,0.10)',
                }}
                aria-label={t.label}
              />
            )
          })}
        </div>
        <div className="mt-2 text-[10px] text-ink-3">
          Active: <span className="text-ink-1 font-medium">{theme.label}</span>
        </div>
      </div>
    </MockShell>
  )
}
